/** The small mutation vocabulary every rule in the battle kernel writes through.
 *
 * Nothing here decides anything: it applies. Every write is journalled (D1), so
 * `undoTo` rolls a whole command back without a clone.
 */

import type { GameEvent } from "../events.js";
import type { Modifier } from "../modifiers.js";
import { F2_MEDITATING } from "./flags.js";
import { resolveStat } from "../modifiers.js";
import type { RngStream } from "../rng/streams.js";
import { nextBounded } from "../rng/pcg32.js";
import {
  F_ALIVE, type BattleState, isAlive,
} from "./state.js";
import { type BattleJournal, jAddI32, jAndNotI32, jOrI32, jSetField, jSetI32 } from "./journal.js";
import { applyDamageToPool } from "./damage.js";

export interface Ctx {
  readonly s: BattleState;
  readonly j: BattleJournal;
  readonly events: GameEvent[];
}

// --------------------------------------------------------------------- rng

export function rollBounded(c: Ctx, stream: RngStream, bound: number): number {
  const cur = c.s.rng[stream]!;
  const [nxt, v] = nextBounded(cur, bound);
  jSetField(c.j, c.s.rng, stream, nxt);
  return v;
}

/** True with probability `bp` basis points. */
export function rollChanceBp(c: Ctx, stream: RngStream, bp: number): boolean {
  if (bp <= 0) return false;
  if (bp >= 10000) return true;
  return rollBounded(c, stream, 10000) < bp;
}

// ------------------------------------------------------------------- stats

/** Max HP per creature after aging and similar `hp` modifiers. */
export function effectiveMaxHp(s: BattleState, i: number): number {
  const mods = s.stacks.mods[i]!;
  let any = false;
  for (const m of mods) if (m.stat === "hp") { any = true; break; }
  if (!any) return s.stacks.maxHp[i]!;
  const v = resolveStat(s.stacks.maxHp[i]!, mods.filter((m) => m.stat === "hp")).value;
  return v < 1 ? 1 : v;
}

// --------------------------------------------------------------- modifiers

export function addMod(c: Ctx, slot: number, mod: Modifier): void {
  const cur = c.s.stacks.mods[slot]!;
  jSetField(c.j, c.s.stacks.mods as unknown as object, slot, [...cur, mod]);
}

export function removeModsBySource(c: Ctx, slot: number, source: string): void {
  const cur = c.s.stacks.mods[slot]!;
  let hit = false;
  for (const m of cur) if (m.source === source) { hit = true; break; }
  if (!hit) return;
  jSetField(c.j, c.s.stacks.mods as unknown as object, slot, cur.filter((m) => m.source !== source));
}

/** Tick round-scoped modifiers on one stack; called at each round boundary. */
export function expireRoundMods(c: Ctx, slot: number): void {
  const cur = c.s.stacks.mods[slot]!;
  let changed = false;
  const out: Modifier[] = [];
  for (const m of cur) {
    const e = m.expires;
    if (e === undefined || e.kind !== "round") { out.push(m); continue; }
    changed = true;
    if (e.rounds > 1) out.push({ ...m, expires: { kind: "round", rounds: e.rounds - 1 } });
  }
  if (changed) jSetField(c.j, c.s.stacks.mods as unknown as object, slot, out);
}

// ------------------------------------------------------------------ damage

export interface DamageResult {
  readonly dealt: number;
  readonly killed: number;
  readonly died: boolean;
}

/** Apply raw HP loss to a stack. Returns what actually landed. */
export function damageStack(c: Ctx, slot: number, damage: number): DamageResult {
  // Meditation: "in this state Sun Serpent doesn't receive damage". Guarded at the
  // one place damage reaches a stack, so it holds for melee, shots, spells,
  // detonation and hazards alike without each of them knowing about it.
  if ((c.s.stacks.flags2[slot]! & F2_MEDITATING) !== 0) {
    return { dealt: 0, killed: 0, died: false };
  }
  const st = c.s.stacks;
  if (!isAlive(st, slot) || damage <= 0) return { dealt: 0, killed: 0, died: false };
  const maxHp = effectiveMaxHp(c.s, slot);
  const r = applyDamageToPool(st.count[slot]!, st.topHp[slot]!, maxHp, damage);
  const newCount = st.count[slot]! - r.killed;
  jSetI32(c.j, st.count, slot, newCount);
  jSetI32(c.j, st.topHp, slot, r.topHp);
  const died = newCount <= 0;
  if (died) {
    jAndNotI32(c.j, st.flags, slot, F_ALIVE);
    c.events.push({ type: "BattleStackDied", stack: slot });
  }
  return { dealt: r.damage, killed: r.killed, died };
}

/** Heal a stack. Wounds first; surplus is discarded unless `resurrectTo` allows regrowth. */
export function healStack(c: Ctx, slot: number, hp: number, resurrectTo: number): number {
  const st = c.s.stacks;
  if (hp <= 0 || !isAlive(st, slot)) return 0;
  const maxHp = effectiveMaxHp(c.s, slot);
  const cap = resurrectTo > st.count[slot]! ? resurrectTo : st.count[slot]!;
  const pool = (st.count[slot]! - 1) * maxHp + st.topHp[slot]!;
  const poolCap = cap * maxHp;
  const healed = Math.min(hp, poolCap - pool);
  if (healed <= 0) return 0;
  const after = pool + healed;
  const count = Math.ceil(after / maxHp);
  jSetI32(c.j, st.count, slot, count);
  jSetI32(c.j, st.topHp, slot, after - (count - 1) * maxHp);
  return healed;
}

export function creditKills(c: Ctx, slot: number, killed: number): void {
  if (killed > 0) jAddI32(c.j, c.s.stacks.killed, slot, killed);
}

export function setHex(c: Ctx, slot: number, hex: number): void {
  jSetI32(c.j, c.s.stacks.hex, slot, hex);
}

export function setFlags(c: Ctx, slot: number, bits: number): void {
  jOrI32(c.j, c.s.stacks.flags, slot, bits);
}

export function clearFlags(c: Ctx, slot: number, bits: number): void {
  jAndNotI32(c.j, c.s.stacks.flags, slot, bits);
}

/** The same two, for the second flag word. See `flags.ts` for why there is one. */
export function setFlags2(c: Ctx, slot: number, bits: number): void {
  jOrI32(c.j, c.s.stacks.flags2, slot, bits);
}

export function clearFlags2(c: Ctx, slot: number, bits: number): void {
  jAndNotI32(c.j, c.s.stacks.flags2, slot, bits);
}
